import 'normalize.css';
import './styles/tokens/tokens.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import LoggingService from 'components/core/logging/LoggingService';
import { bootSequence as intlBootSequence } from 'utils/intlUtils';

import App from './App';

// Initialize error logging
LoggingService.init();

// Conditional MSW import for mock handlers
const initializeMSW = async () => {
  if (process.env.REACT_APP_FEATURE_MOCKS_ENABLED === 'true') {
    try {
      // Dynamic import prevents MSW from being bundled in production
      const { handlers } = await import('./mocks/handlers');

      if (handlers.length > 0) {
        const { startMockWorker } = await import('./mocks/browser');

        // Start MSW with mock handlers
        await startMockWorker();

        // eslint-disable-next-line no-console
        console.log(`🎭 MSW active with ${handlers.length} mock handler(s)`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('MSW initialization failed - app will continue without mocks:', error);
    }
  }
};

// Initialize app
const renderApp = () => {
  const container = document.querySelector('#root');
  if (!container) {
    throw new Error('Init Error: No container to renderApp!');
  }

  const root = createRoot(container, {
    onCaughtError: error => {
      LoggingService.error({
        error: error instanceof Error ? error : undefined,
        message: 'Index.tsx: onCaughtError',
      });

      throw error;
    },
    onUncaughtError: error => {
      LoggingService.error({
        error: error instanceof Error ? error : undefined,
        message: 'Index.tsx: onUncaughtError',
      });
    },
  });

  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
};

const run = async () => {
  try {
    // Initialize MSW if mock operations exist
    await initializeMSW();

    // Let's go
    await intlBootSequence();

    renderApp();

    if (!process.env.REACT_APP_GRAPH_BASE) {
      throw new Error('Provide REACT_APP_GRAPH_BASE');
    }
  } catch (error: any) {
    LoggingService.error({
      error,
      message: 'Index.tsx: run error',
    });
  }
};

void run();
